import { FunctionComponent } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { FindingCitation } from 'types/findings';
import { citationLabel, citationReferenceLabel, parseCitationUrn } from 'utils/citationLabel';
import { CitationEvidencePanel } from 'components/CitationEvidence';
import './CitationDetailsDialog.css';

interface CitationDetailsDialogProps {
  citation: FindingCitation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional auth token forwarded to the US-266 citation asset API. */
  token?: string;
  /** Optional API host override forwarded to the US-266 citation asset API. */
  address?: string | null;
}

function verifiedOfficialUrl(citation: FindingCitation): string | null {
  if (
    !citation.url
    || citation.verified_official_url !== true
    || !citation.provenance?.source_type?.startsWith('official_')
  ) {
    return null;
  }

  try {
    const url = new URL(citation.url);
    const trustedOfficialPdf = url.protocol === 'https:'
      && url.hostname === 'assets.gov.ie'
      && url.pathname.startsWith('/static/documents/')
      && url.pathname.endsWith('.pdf')
      && !url.hash
      && !url.port
      && !url.username
      && !url.password;
    return trustedOfficialPdf ? url.toString() : null;
  } catch {
    return null;
  }
}

export const CitationDetailsDialog: FunctionComponent<CitationDetailsDialogProps> = ({
  citation,
  open,
  onOpenChange,
  token,
  address = null,
}) => {
  if (!citation) {
    return null;
  }

  const parsedUrn = parseCitationUrn(citation.urn);
  const clause = citation.clause
    || (parsedUrn ? citationReferenceLabel(parsedUrn) : 'Clause unavailable');
  const excerpt = citation.excerpt || citation.snippet;
  const documentTitle = citation.document_title || citationLabel(citation.urn);
  const provenance = citation.provenance;
  const hasProvenance = Boolean(
    provenance?.authority
      || provenance?.document_code
      || provenance?.edition
      || provenance?.source_type
      || provenance?.page
      || provenance?.locator
      || provenance?.text_normalization
  );
  const officialUrl = verifiedOfficialUrl(citation);

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface data-testid="citation-details-dialog">
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close" disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  aria-label="Close citation details"
                  icon={<Dismiss24Regular />}
                />
              </DialogTrigger>
            }
          >
            Citation details
          </DialogTitle>
          <DialogContent className="citation-details">
            <div>
              <span className="citation-details__label">Document</span>
              <h3 className="citation-details__document">{documentTitle}</h3>
            </div>
            <div>
              <span className="citation-details__label">Clause / section</span>
              <p className="citation-details__value">{clause}</p>
            </div>
            <div>
              <span className="citation-details__label">Relevant excerpt</span>
              {excerpt ? (
                <blockquote className="citation-details__excerpt">{excerpt}</blockquote>
              ) : (
                <p className="citation-details__fallback">
                  The source did not provide an excerpt for this citation.
                </p>
              )}
            </div>
            <div>
              <span className="citation-details__label">Source provenance</span>
              {hasProvenance ? (
                <dl className="citation-details__provenance">
                  {provenance?.authority && <><dt>Authority</dt><dd>{provenance.authority}</dd></>}
                  {provenance?.document_code && <><dt>Document code</dt><dd>{provenance.document_code}</dd></>}
                  {provenance?.edition && <><dt>Edition</dt><dd>{provenance.edition}</dd></>}
                  {provenance?.source_type && <><dt>Source type</dt><dd>{provenance.source_type}</dd></>}
                  {provenance?.page && <><dt>Page</dt><dd>{provenance.page}</dd></>}
                  {provenance?.locator && <><dt>Locator</dt><dd>{provenance.locator}</dd></>}
                  {provenance?.text_normalization && (
                    <><dt>Text normalization</dt><dd>{provenance.text_normalization}</dd></>
                  )}
                </dl>
              ) : (
                <p className="citation-details__fallback">Source provenance is unavailable.</p>
              )}
            </div>
            <CitationEvidencePanel citation={citation} token={token} address={address} />
          </DialogContent>
          <DialogActions>
            {officialUrl && (
              <a
                className="citation-details__source-link"
                href={officialUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open official document
              </a>
            )}
            <DialogTrigger action="close" disableButtonEnhancement>
              <Button appearance="secondary">Close</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
